import { useState, useMemo, useEffect, useCallback } from 'react';
import { parseLocalDate } from '@/utils/dateUtils';
import { format } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, Search, MapPin, Home, User, Calendar, UserPlus, ChevronDown, MessageCircle } from 'lucide-react';
import { createOrGetChat } from '@/hooks/useChats';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { usePlatform } from '@/hooks/usePlatform';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { getSubcategoriesForSport } from '@/utils/sportRules';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import TournamentCard from '@/components/TournamentCard';
import LeagueCard from '@/components/LeagueCard';
import { useUpcomingEventsByType } from '@/hooks/useEvents';

interface ListingWithProfile {
  id: string;
  event_id: string;
  user_id: string;
  category: string | null;
  subcategory: string | null;
  preferred_position: string;
  profile?: {
    full_name: string | null;
    avatar_url: string | null;
    skill_level: string | null;
  };
}

interface WaitlistPair {
  id: string;
  category: string | null;
  subcategory: string | null;
  drive: ListingWithProfile | null;
  reves: ListingWithProfile | null;
}

interface EventWaitlistGroup {
  eventId: string;
  title: string;
  location: string;
  event_type: string;
  sport: string;
  start_date: string;
  end_date: string | null;
  entry_fee: number;
  address: string;
  country: string;
  state: string;
  pairs: WaitlistPair[];
}

function buildPairsFromListings(listings: ListingWithProfile[]): WaitlistPair[] {
  // Group by category+subcategory
  const groups = new Map<string, { drives: ListingWithProfile[]; reves: ListingWithProfile[] }>();
  for (const l of listings) {
    const key = `${l.category || ''}|${l.subcategory || ''}`;
    if (!groups.has(key)) groups.set(key, { drives: [], reves: [] });
    const g = groups.get(key)!;
    if (l.preferred_position === 'drive') g.drives.push(l);
    else g.reves.push(l);
  }

  const pairs: WaitlistPair[] = [];
  for (const [, group] of groups) {
    const maxLen = Math.max(group.drives.length, group.reves.length);
    for (let i = 0; i < maxLen; i++) {
      const d = group.drives[i] || null;
      const r = group.reves[i] || null;
      pairs.push({
        id: d?.id || r?.id || `pair-${pairs.length}`,
        category: (d || r)?.category || null,
        subcategory: (d || r)?.subcategory || null,
        drive: d,
        reves: r,
      });
    }
  }
  return pairs;
}

const PartnerSearchAllPage = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const { user } = useAuth();
  const { isIOS, isAndroid, isNative } = usePlatform();
  const [waitlistGroups, setWaitlistGroups] = useState<EventWaitlistGroup[]>([]);
  const [loadingWaitlists, setLoadingWaitlists] = useState(true);
  const [selectedSport, setSelectedSport] = useState('todos');
  const [selectedEventType, setSelectedEventType] = useState('todos');
  const [selectedCategory, setSelectedCategory] = useState('todos');
  const [selectedSubcategory, setSelectedSubcategory] = useState('todos');

  const { events: tournamentEvents, loading: loadingTournaments } = useUpcomingEventsByType('tournament');
  const { events: leagueEvents, loading: loadingLeagues } = useUpcomingEventsByType('league');

  // Fetch active waitlists with full listing data
  useEffect(() => {
    const fetchWaitlists = async () => {
      try {
        const { data: listings } = await supabase
          .from('partner_search_listings')
          .select('id, event_id, user_id, category, subcategory, preferred_position')
          .eq('status', 'searching');

        if (!listings || listings.length === 0) {
          setWaitlistGroups([]);
          setLoadingWaitlists(false);
          return;
        }

        // Get unique event ids and user ids
        const eventIds = [...new Set(listings.map(l => l.event_id))];
        const userIds = [...new Set(listings.map(l => l.user_id))];

        // Fetch events and profiles in parallel
        const [eventsRes, profilesRes] = await Promise.all([
          supabase.from('events').select('id, title, location, event_type, sport, start_date, end_date, entry_fee, rules, address').in('id', eventIds),
          supabase.from('profiles').select('user_id, full_name, avatar_url, skill_level').in('user_id', userIds),
        ]);

        const eventMap = new Map(eventsRes.data?.map(e => [e.id, e]) || []);
        const profileMap = new Map(profilesRes.data?.map(p => [p.user_id, p]) || []);

        // Enrich listings
        const enriched: ListingWithProfile[] = listings.map(l => ({
          ...l,
          profile: profileMap.get(l.user_id) || undefined,
        }));

        // Group by event
        const byEvent = new Map<string, ListingWithProfile[]>();
        for (const l of enriched) {
          if (!byEvent.has(l.event_id)) byEvent.set(l.event_id, []);
          byEvent.get(l.event_id)!.push(l);
        }

        const groups: EventWaitlistGroup[] = [];
        for (const [eventId, eventListings] of byEvent) {
          const ev = eventMap.get(eventId);
          if (!ev) continue;

          // Parse country/state from rules JSON
          let country = '';
          let state = '';
          if (ev.rules) {
            try {
              const rulesData = typeof ev.rules === 'string' ? JSON.parse(ev.rules) : ev.rules;
              if (rulesData.country) country = rulesData.country;
              if (rulesData.state) state = rulesData.state;
            } catch {}
          }
          // Fallback: parse from address
          if (!country && ev.address) {
            const parts = ev.address.split(',').map((s: string) => s.trim());
            if (parts.length >= 2) {
              state = parts[parts.length - 2] || '';
              country = parts[parts.length - 1] || '';
            }
          }

          groups.push({
            eventId,
            title: ev.title,
            location: ev.location,
            event_type: ev.event_type,
            sport: ev.sport || '',
            start_date: ev.start_date,
            end_date: ev.end_date || null,
            entry_fee: Number(ev.entry_fee) || 0,
            address: ev.address || '',
            country,
            state,
            pairs: buildPairsFromListings(eventListings),
          });
        }

        // Sort by most listings first
        groups.sort((a, b) => b.pairs.length - a.pairs.length);
        setWaitlistGroups(groups);
      } catch (err) {
        console.error('[PartnerSearchAll] Error:', err);
      } finally {
        setLoadingWaitlists(false);
      }
    };

    fetchWaitlists();
  }, []);

  // Map tournaments to TournamentCard format (same as ExploreEvents)
  const mappedTournaments = useMemo(() => {
    return tournamentEvents.map(event => ({
      id: event.id,
      title: event.title,
      sport: event.sport,
      date: event.start_date,
      endDate: event.end_date || '',
      location: event.location,
      registrationFee: Number(event.entry_fee) || 0,
      organizer: event.organizer_company_name || event.organizer_full_name || 'Organizador',
      genderCategories: event.genderCategories && event.genderCategories.length > 0
        ? event.genderCategories
        : event.subcategories && Object.keys(event.subcategories).length > 0
          ? Object.keys(event.subcategories)
          : event.gender_category
            ? event.gender_category.split(',').map(s => s.trim())
            : [],
      subcategories: event.subcategories && Object.keys(event.subcategories).length > 0
        ? event.subcategories
        : {},
      matchType: (event.match_type as 'singles' | 'dobles' | 'ambos') || 'dobles',
      registrationDeadline: event.registration_deadline || undefined,
      imageUrl: (event as any).image_url || undefined,
      address: event.address || undefined,
      postal_code: event.postal_code || undefined,
    }));
  }, [tournamentEvents]);

  // Map leagues to LeagueCard format (same as ExploreLeagues)
  const mappedLeagues = useMemo(() => {
    return leagueEvents.map(event => ({
      id: event.id,
      title: event.title,
      sport: event.sport,
      date: event.start_date,
      endDate: event.end_date || '',
      location: event.location,
      registrationFee: Number(event.entry_fee) || 0,
      organizer: event.organizer_company_name || event.organizer_full_name || 'Organizador',
      genderCategories: event.genderCategories && event.genderCategories.length > 0
        ? event.genderCategories
        : event.subcategories && Object.keys(event.subcategories).length > 0
          ? Object.keys(event.subcategories)
          : event.gender_category
            ? event.gender_category.split(',').map(s => s.trim())
            : event.category ? [event.category] : [],
      subcategories: event.subcategories && Object.keys(event.subcategories).length > 0
        ? event.subcategories
        : {},
      matchType: (event.match_type as 'singles' | 'dobles' | 'ambos') || 'dobles',
      leagueFormat: (event.league_format === 'escalera' ? 'escalera-piramidal' : event.league_format) as 'round-robin' | 'escalera-piramidal' | 'sumatoria-juegos',
      registrationDeadline: event.registration_deadline || undefined,
      imageUrl: (event as any).image_url || undefined,
      address: event.address || undefined,
      postal_code: event.postal_code || undefined,
      total_weeks: event.total_weeks || undefined,
      matches_per_week: event.matches_per_week || undefined,
      maxParticipants: event.max_participants || 0,
      participants: 0,
    }));
  }, [leagueEvents]);

  // Derived subcategory options
  const subcategoryOptions = useMemo(() => {
    if (selectedCategory === 'todos' || selectedSport === 'todos') return [];
    const sportMap: Record<string, string> = { 'Pádel': 'Pádel', 'Tenis': 'Tenis', 'Pickleball': 'Pickleball' };
    const catMap: Record<string, string> = { 'Masculina': 'masculina', 'Femenina': 'femenina', 'Mixta': 'mixta' };
    const sport = sportMap[selectedSport];
    const cat = catMap[selectedCategory];
    if (!sport || !cat) return [];
    return getSubcategoriesForSport(sport as any, cat as any);
  }, [selectedSport, selectedCategory]);

  // Reset subcategory when sport or category changes
  useEffect(() => {
    setSelectedSubcategory('todos');
  }, [selectedSport, selectedCategory]);

  // Normalize sport for comparison
  const normSport = (s: string) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  // Filter waitlist groups
  const filteredWaitlistGroups = useMemo(() => {
    return waitlistGroups.filter(g => {
      if (selectedSport !== 'todos' && normSport(g.sport) !== normSport(selectedSport)) return false;
      if (selectedEventType !== 'todos' && g.event_type !== selectedEventType) return false;
      const matchingPairs = g.pairs.filter(p => {
        if (selectedCategory !== 'todos') {
          const pairCat = (p.category || '').toLowerCase();
          if (!pairCat.includes(selectedCategory.toLowerCase())) return false;
        }
        if (selectedSubcategory !== 'todos') {
          const pairSub = (p.subcategory || '').toLowerCase();
          if (pairSub !== selectedSubcategory.toLowerCase()) return false;
        }
        return true;
      });
      return matchingPairs.length > 0;
    }).map(g => {
      if (selectedCategory === 'todos' && selectedSubcategory === 'todos') return g;
      const filteredPairs = g.pairs.filter(p => {
        if (selectedCategory !== 'todos') {
          const pairCat = (p.category || '').toLowerCase();
          if (!pairCat.includes(selectedCategory.toLowerCase())) return false;
        }
        if (selectedSubcategory !== 'todos') {
          const pairSub = (p.subcategory || '').toLowerCase();
          if (pairSub !== selectedSubcategory.toLowerCase()) return false;
        }
        return true;
      });
      return { ...g, pairs: filteredPairs };
    });
  }, [waitlistGroups, selectedSport, selectedEventType, selectedCategory, selectedSubcategory]);

  const filteredTournaments = useMemo(() => {
    if (selectedEventType === 'league') return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return mappedTournaments.filter(e => {
      const eventDate = parseLocalDate(e.date);
      eventDate.setHours(0, 0, 0, 0);
      const hasNotStartedYet = eventDate >= today;
      const deadline = e.registrationDeadline ? parseLocalDate(e.registrationDeadline) : null;
      if (deadline) deadline.setHours(0, 0, 0, 0);
      const hasOpenRegistrations = !deadline || deadline >= today;
      const matchesSport = selectedSport === 'todos' || normSport(e.sport) === normSport(selectedSport);
      return hasNotStartedYet && hasOpenRegistrations && matchesSport;
    });
  }, [mappedTournaments, selectedSport, selectedEventType]);

  const filteredLeagues = useMemo(() => {
    if (selectedEventType === 'tournament') return [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return mappedLeagues.filter(e => {
      const eventDate = parseLocalDate(e.date);
      eventDate.setHours(0, 0, 0, 0);
      const hasNotStartedYet = eventDate >= today;
      const deadline = e.registrationDeadline ? parseLocalDate(e.registrationDeadline) : null;
      if (deadline) deadline.setHours(0, 0, 0, 0);
      const hasOpenRegistrations = !deadline || deadline >= today;
      const matchesSport = selectedSport === 'todos' || normSport(e.sport) === normSport(selectedSport);
      return hasNotStartedYet && hasOpenRegistrations && matchesSport;
    });
  }, [mappedLeagues, selectedSport, selectedEventType]);

  const formatEventType = (type: string) => {
    if (type === 'tournament') return language === 'es' ? 'Torneo' : 'Tournament';
    if (type === 'league') return language === 'es' ? 'Liga' : 'League';
    return type;
  };

  const getInitials = (name?: string | null) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const renderSlot = (listing: ListingWithProfile | null, positionLabel: string, eventId: string, eventType: string) => {
    const occupied = !!listing;
    const isCurrentUser = listing?.user_id === user?.id;

    const handleAvailableClick = () => {
      navigate('/', { state: { showEventDetail: true, event: { id: eventId } } });
    };

    return (
      <div
        className={`flex-1 flex flex-col items-center gap-0.5 p-1 rounded-lg transition-all duration-200 ${
          !occupied ? 'cursor-pointer hover:bg-green-700/5 active:scale-[0.97]' : ''
        }`}
        onClick={() => !occupied && handleAvailableClick()}
      >
        <span className={`text-[10px] font-semibold uppercase tracking-wider ${occupied ? 'text-primary' : 'text-green-700'}`}>
          {positionLabel}
        </span>

        {occupied ? (
          <>
            <Avatar
              className="h-8 w-8 ring-2 ring-primary/20 cursor-pointer"
              onClick={(e) => { e.stopPropagation(); navigate(`/profile/${listing.user_id}`, { state: { from: '/partner-search' } }); }}
            >
              <AvatarImage src={listing.profile?.avatar_url || undefined} />
              <AvatarFallback className="bg-gradient-to-br from-primary to-primary/70 text-primary-foreground font-bold text-xs">
                {getInitials(listing.profile?.full_name)}
              </AvatarFallback>
            </Avatar>
            <span className="text-xs font-medium text-foreground truncate max-w-[90px]">
              {listing.profile?.full_name || (language === 'es' ? 'Jugador' : 'Player')}
            </span>
            {isCurrentUser && (
              <Badge variant="outline" className="text-[10px] px-2 py-0 border-primary/40 text-primary">
                {language === 'es' ? 'Tú' : 'You'}
              </Badge>
            )}
          </>
        ) : (
          <>
            <div className="h-8 w-8 rounded-full border-2 border-dashed border-green-700 flex items-center justify-center">
              <UserPlus className="h-4 w-4 text-green-700" />
            </div>
            <span className="text-xs text-green-700">
              {language === 'es' ? 'Disponible' : 'Available'}
            </span>
          </>
        )}
      </div>
    );
  };

  const loading = loadingWaitlists || loadingTournaments || loadingLeagues;

  return (
    <div className="min-h-screen bg-background pb-20">
      {/* Header */}
      <div
        className="sticky top-0 z-10 bg-background border-b px-4 pb-3 flex items-center gap-3"
        style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      >
        <button onClick={() => navigate(-1)} className="p-1">
          <ArrowLeft className="h-5 w-5 text-foreground" />
        </button>
        <h1 className="text-lg font-bold text-foreground">
          {language === 'es' ? 'Buscar pareja' : 'Find a partner'}
        </h1>
      </div>

      <div className="px-4 py-3 space-y-4">
        {/* Filters as dropdowns in one row */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {/* Event Type */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="shrink-0 gap-1 text-xs">
                {selectedEventType === 'todos'
                  ? (language === 'es' ? 'Evento' : 'Event')
                  : selectedEventType === 'tournament'
                    ? (language === 'es' ? 'Torneo' : 'Tournament')
                    : (language === 'es' ? 'Liga' : 'League')}
                <ChevronDown className="h-3 w-3 opacity-60" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-36 p-1" align="start">
              {[
                { value: 'todos', label: language === 'es' ? 'Todos' : 'All' },
                { value: 'tournament', label: language === 'es' ? 'Torneo' : 'Tournament' },
                { value: 'league', label: language === 'es' ? 'Liga' : 'League' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setSelectedEventType(opt.value)}
                  className={`w-full text-left px-3 py-1.5 text-sm rounded-sm transition-colors ${
                    selectedEventType === opt.value ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </PopoverContent>
          </Popover>

          {/* Sport */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="shrink-0 gap-1 text-xs">
                {selectedSport === 'todos' ? (language === 'es' ? 'Deporte' : 'Sport') : selectedSport}
                <ChevronDown className="h-3 w-3 opacity-60" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-36 p-1" align="start">
              {[
                { value: 'todos', label: language === 'es' ? 'Todos' : 'All' },
                { value: 'Pádel', label: 'Pádel' },
                { value: 'Tenis', label: 'Tenis' },
                { value: 'Pickleball', label: 'Pickleball' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setSelectedSport(opt.value)}
                  className={`w-full text-left px-3 py-1.5 text-sm rounded-sm transition-colors ${
                    selectedSport === opt.value ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </PopoverContent>
          </Popover>

          {/* Category */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="shrink-0 gap-1 text-xs">
                {selectedCategory === 'todos' ? (language === 'es' ? 'Categoría' : 'Category') : selectedCategory}
                <ChevronDown className="h-3 w-3 opacity-60" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-36 p-1" align="start">
              {[
                { value: 'todos', label: language === 'es' ? 'Todas' : 'All' },
                { value: 'Masculina', label: 'Masculina' },
                { value: 'Femenina', label: 'Femenina' },
                { value: 'Mixta', label: 'Mixta' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setSelectedCategory(opt.value)}
                  className={`w-full text-left px-3 py-1.5 text-sm rounded-sm transition-colors ${
                    selectedCategory === opt.value ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </PopoverContent>
          </Popover>

          {/* Subcategory - only when sport + category selected */}
          {selectedCategory !== 'todos' && selectedSport !== 'todos' && subcategoryOptions.length > 0 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="shrink-0 gap-1 text-xs">
                  {selectedSubcategory === 'todos' ? (language === 'es' ? 'Subcat.' : 'Subcat.') : selectedSubcategory}
                  <ChevronDown className="h-3 w-3 opacity-60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-36 p-1" align="start">
                <button
                  onClick={() => setSelectedSubcategory('todos')}
                  className={`w-full text-left px-3 py-1.5 text-sm rounded-sm transition-colors ${
                    selectedSubcategory === 'todos' ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
                  }`}
                >
                  {language === 'es' ? 'Todas' : 'All'}
                </button>
                {subcategoryOptions.map(sub => (
                  <button
                    key={sub}
                    onClick={() => setSelectedSubcategory(sub)}
                    className={`w-full text-left px-3 py-1.5 text-sm rounded-sm transition-colors ${
                      selectedSubcategory === sub ? 'bg-primary text-primary-foreground' : 'hover:bg-accent'
                    }`}
                  >
                    {sub}
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          )}
        </div>

        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-20 bg-muted animate-pulse rounded-lg" />
            ))}
          </div>
        ) : (
          <>
            {/* Active Waitlists with paired slots */}
            <section>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                {language === 'es' ? 'Listas de espera activas' : 'Active waiting lists'}
              </h2>
              {filteredWaitlistGroups.length === 0 ? (
                <Card>
                  <CardContent className="p-4 text-center text-sm text-muted-foreground">
                    {language === 'es'
                      ? 'No hay listas de espera activas en este momento'
                      : 'No active waiting lists at this time'}
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {filteredWaitlistGroups.flatMap(group => {
                    const formattedDate = (() => {
                      try {
                        const loc = language === 'es' ? es : enUS;
                        const startD = parseLocalDate(group.start_date);
                        if (group.end_date) {
                          const endD = parseLocalDate(group.end_date);
                          const sameDay = format(startD, 'yyyy-MM-dd') === format(endD, 'yyyy-MM-dd');
                          if (!sameDay) {
                            const sameYear = startD.getFullYear() === endD.getFullYear();
                            const startFmt = sameYear ? format(startD, 'd MMM', { locale: loc }) : format(startD, 'd MMM yyyy', { locale: loc });
                            return `${startFmt} - ${format(endD, 'd MMM yyyy', { locale: loc })}`;
                          }
                        }
                        return format(startD, 'd MMM yyyy', { locale: loc });
                      } catch { return ''; }
                    })();
                    const pricePerPerson = group.entry_fee > 0 ? Math.round(group.entry_fee / 2) : 0;

                    return group.pairs.map(pair => {
                      const label = (() => {
                        const cat = (pair.category || '').trim();
                        const sub = (pair.subcategory || '').trim();
                        return cat && sub ? `${cat} ${sub}` : cat || sub || '';
                      })();
                      const isUserInPair = pair.drive?.user_id === user?.id || pair.reves?.user_id === user?.id;

                      return (
                        <Card
                          key={pair.id}
                          className={`overflow-hidden transition-all duration-200 ${
                            isUserInPair ? 'border-primary/30 ring-1 ring-primary/20' : ''
                          }`}
                        >
                          <CardContent className="p-2.5 space-y-1.5">
                            {/* Header: title | date, category below + chat button */}
                            <div className="flex justify-between items-start">
                              <div className="space-y-0.5 min-w-0 flex-1">
                                <div className="flex items-center gap-1.5 min-w-0">
                                  <p
                                    className="text-sm font-bold text-foreground truncate cursor-pointer hover:text-primary transition-colors"
                                    onClick={() => navigate(`/partner-search/${group.eventId}`)}
                                  >
                                    {group.title}
                                  </p>
                                  {formattedDate && (
                                    <>
                                      <span className="text-muted-foreground font-light">|</span>
                                      <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                                        {formattedDate}
                                      </span>
                                    </>
                                  )}
                                </div>
                                {label && (
                                  <p className="text-xs text-primary font-medium">{label}</p>
                                )}
                              </div>
                              {(() => {
                                const otherPlayer = [pair.drive, pair.reves].find(
                                  (slot) => slot && slot.user_id !== user?.id
                                );
                                if (!otherPlayer || !user) return null;
                                return (
                                  <button
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        const chatId = await createOrGetChat(otherPlayer.user_id);
                                        navigate(`/chat/${chatId}`);
                                      } catch (err) {
                                        console.error('Error starting chat:', err);
                                      }
                                    }}
                                    className="p-1.5 rounded-full hover:bg-muted transition-colors shrink-0"
                                    title={language === 'es' ? 'Enviar mensaje' : 'Send message'}
                                  >
                                    <MessageCircle className="h-4 w-4 text-primary" />
                                  </button>
                                );
                              })()}
                            </div>

                            {/* Slots: Drive / Revés */}
                            <div className="flex gap-2">
                              {renderSlot(pair.reves, language === 'es' ? 'Revés' : 'Backhand', group.eventId, group.event_type)}
                              {renderSlot(pair.drive, 'Drive', group.eventId, group.event_type)}
                            </div>

                            {/* Footer: location + price */}
                            <div className="pt-2 border-t border-border flex justify-between items-end">
                              <div className="min-w-0">
                                <p className="text-[11px] font-semibold text-foreground uppercase truncate flex items-center gap-1">
                                  <MapPin className="h-3 w-3 shrink-0 text-primary" />
                                  {group.location}
                                </p>
                                {(group.country || group.state) && (
                                  <p className="text-[10px] text-muted-foreground ml-4 truncate">
                                    {[group.state, group.country].filter(Boolean).join(', ')}
                                  </p>
                                )}
                              </div>
                              {pricePerPerson > 0 && (
                                <div className="bg-green-700 rounded-md px-3 py-1 text-center shrink-0">
                                  <p className="text-sm font-bold text-[#FFF8E7]">${pricePerPerson}</p>
                                  <p className="text-[8px] text-[#FFF8E7]/90 -mt-0.5 leading-none">
                                    {language === 'es' ? 'por persona' : 'per person'}
                                  </p>
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      );
                    });
                  })}
                </div>
              )}
            </section>

            {/* Upcoming Tournaments */}
            {filteredTournaments.length > 0 && (
              <section>
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  {language === 'es' ? 'Torneos' : 'Tournaments'}
                </h2>
                <div className="space-y-3">
                  {filteredTournaments.map(tournament => (
                    <TournamentCard
                      key={tournament.id}
                      tournament={tournament}
                      onTournamentClick={() => navigate('/', { state: { showEventDetail: true, event: { id: tournament.id } } })}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Upcoming Leagues */}
            {filteredLeagues.length > 0 && (
              <section>
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  {language === 'es' ? 'Ligas' : 'Leagues'}
                </h2>
                <div className="space-y-3">
                  {filteredLeagues.map(league => (
                    <LeagueCard
                      key={league.id}
                      league={league}
                      onLeagueClick={() => navigate('/', { state: { showEventDetail: true, event: { id: league.id } } })}
                    />
                  ))}
                </div>
              </section>
            )}

            {/* Empty state for events */}
            {filteredTournaments.length === 0 && filteredLeagues.length === 0 && (
              <section>
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  {language === 'es' ? 'Próximos torneos y ligas' : 'Upcoming tournaments & leagues'}
                </h2>
                <Card>
                  <CardContent className="p-4 text-center text-sm text-muted-foreground">
                    {language === 'es' ? 'No hay eventos próximos' : 'No upcoming events'}
                  </CardContent>
                </Card>
              </section>
            )}
          </>
        )}
      </div>

      {/* Bottom Navigation */}
      <nav
        className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 z-50"
        style={{ paddingBottom: (isIOS || isAndroid) && isNative ? '20px' : '0px' }}
      >
        <div className="grid grid-cols-4 h-14 md:h-16">
          {[
            { id: 'dashboard', label: t('nav.home'), icon: Home },
            { id: 'events', label: t('nav.explore'), icon: Search },
            { id: 'my-events', label: t('nav.myEvents'), icon: Calendar },
            { id: 'profile', label: t('nav.profile'), icon: User },
          ].map(item => {
            const Icon = item.icon;
            return (
              <Button
                key={item.id}
                variant="ghost"
                onClick={() => navigate('/', { state: { tab: item.id } })}
                className="flex flex-col items-center justify-center h-full rounded-none space-y-0.5 md:space-y-1 text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              >
                <Icon className="h-4 w-4 md:h-5 md:w-5" />
                <span className="text-[10px] md:text-xs font-medium">{item.label}</span>
              </Button>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default PartnerSearchAllPage;
